<script setup>
// "Outstanding entry fee — pay now" banner for competition detail pages. Given
// this competition's sourceId, shows the current user's Due obligation and opens
// the interactive PayEntryFeeDialog. Renders nothing when there's nothing owed.
import PayEntryFeeDialog from "~/components/payments/PayEntryFeeDialog.vue";
import {usePaymentsDisplay} from "~/composables/usePaymentsDisplay";
import {useEntryFeeObligation} from "~/composables/useEntryFeeObligation";
import {ref, onMounted, toRef, watch} from "vue";
import {useAuthService} from "~/composables/useAuthService";
import {entryFeeAssignedToMe, entryFeeAssignedLine, entryFeeOwedTitle} from "~/composables/usePayingHonestly";

const props = defineProps({
  sourceId: {type: String, required: true},
});

const {money} = usePaymentsDisplay();
// Pass a ref so the banner re-fetches when the viewed cycle changes (league page)
const {obligation, refresh} = useEntryFeeObligation(toRef(props, 'sourceId'));
const payOpen = ref(false);
// Assigned to this member (R2 batch B): they pay FOR a team, so say whose and why.
const {user} = useAuthService();

onMounted(refresh);

// Refresh only after the dialog CLOSES: an immediate refresh on 'paid' would
// null the obligation, unmount the banner and tear down the dialog's
// "you're all paid up" state while the member is still looking at it.
watch(payOpen, (open) => {
  if (!open) refresh();
});
</script>

<template>
  <div v-if="obligation" class="ofb tl-card">
    <div class="ofb-body">
      <v-icon size="20" color="primary">mdi-credit-card-clock-outline</v-icon>
      <div class="flex-grow-1">
        <template v-if="entryFeeAssignedToMe(obligation, user?.id)">
          <div class="ofb-title">{{ entryFeeOwedTitle(obligation, user?.id) }}</div>
          <div class="ofb-sub">{{ entryFeeAssignedLine(obligation) }}</div>
        </template>
        <template v-else>
          <div class="ofb-title">Outstanding entry fee</div>
          <div class="ofb-sub">
            {{ money(obligation.amountMinor, obligation.currency) }} to enter {{ obligation.entityName }}<template
                v-if="obligation.teamPlayerCount > 1">. One payment covers your whole team ({{ obligation.teamName }}); either of you can pay.</template>
          </div>
        </template>
      </div>
      <v-btn color="primary" size="small" prepend-icon="mdi-lock" @click="payOpen = true">
        Pay {{ money(obligation.amountMinor, obligation.currency) }}
      </v-btn>
    </div>

    <pay-entry-fee-dialog v-model="payOpen" :obligation="obligation"/>
  </div>
</template>

<style scoped lang="scss">
.ofb {
  border-left: 3px solid rgb(var(--v-theme-primary));
  padding: 14px 16px;
  margin-bottom: 16px;
}

.ofb-body {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ofb-title {
  font-size: 13.5px;
  font-weight: 700;
}

.ofb-sub {
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 2px;
}
</style>
